import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ItemModel } from 'src/app/items/item.model';
import { PrequalificationModel } from 'src/app/models/prequalification.model';
import { UtilsService } from 'src/app/shared/utils.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { AppService } from 'src/app/app.service';
import { RBACService } from '../../../shared/rbac.service';
import moment from 'moment';

@Component({
    selector: 'app-contractors-prequalifications-edit',
    templateUrl: './contractors-prequalifications-edit.component.html',
    styleUrls: ['./contractors-prequalifications-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractorsPrequalificationsEditComponent implements OnInit {

  prequalification = new PrequalificationModel();

  items: ItemModel[];

  templates: PrequalificationModel[];

  contractorId: number;

  inProgress: boolean = false;

  currentDate: moment.Moment = moment();
  expiryDate: moment.Moment;
  originalExpiryDate: number | null = null;

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canDelete: boolean = false;

  constructor(
    private route: ActivatedRoute,
    public utils: UtilsService,
    private router: Router,
    private api: ApiRequestService,
    public app: AppService,
    private rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canDelete = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_DELETE)
    ], true);

    // this.prequalification.id = Number(this.route.snapshot.params['prequal_id']);
    // if ( this.prequalification.id ) {
    //   this.getPreQualification(this.prequalification.id);
    // }

    // "Copy From Template" list: the API accepts templates view, or the parent create/edit permission.
    if (this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TEMPLATES_CONTRACTOR_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_VIEW),
      RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_CREATE),
      RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_EDIT)
    ])) {
      this.getPreQualificationTemplates();
    }
    // The pre-qualification item catalogue is global and readable by every authenticated user.
    this.getPreQualificationSelectableItems();

    this.contractorId = Number(this.route.snapshot.parent.params.contractor_id);
    if ( this.contractorId ) {
      // Only fetch existing pre-qualification if user has view or edit permission.
      // Create-only users start with a blank form.
      const canViewOrEdit = this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_VIEW),
        RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_EDIT)
      ]);
      if (canViewOrEdit) {
        this.getPreQualification().then(() => {
          this.prequalification.contractor_id = this.contractorId;
        });
      } else {
        // Create-only user: check if a pre-qualification already exists before showing the form
        this.api.makeRequest('get', `v2/contractor-pre-qualifications/${this.contractorId}/exists`)
          .then((response: { exists: boolean }) => {
            if (response.exists) {
              this.rbac.showGenericAccessDeniedMessage('A pre-qualification already exists for this contractor. You need view or edit permission to manage it.');
              this.router.navigate(['/contractors', this.contractorId, 'details']);
            } else {
              this.prequalification.contractor_id = this.contractorId;
            }
          });
      }
    }
  }

  private getPreQualificationTemplates() {
    this.api.makeRequest('get', 'v1/prequalifications', {}, { template: true, order_by: 'name', order: 'asc' })
    .then((response) => {
      this.templates = response.data;
    });
  }

  private getPreQualification() {
    return this.api.makeRequest('get', `v2/contractor-pre-qualifications/${this.contractorId}`)
    .then((response) => {
      this.prequalification = response;
      this.prequalification.items = this.prequalification.items.map((item) => item.id);
      // Set the expiry date
      if ( this.prequalification.expires_at ) {
        const expiresAt = moment.unix(this.prequalification.expires_at).tz(this.app.account.timezone);

        this.expiryDate = expiresAt.clone().startOf('day');
        this.originalExpiryDate = this.expiryDate.clone().unix();
      }
    })
    .catch(() => {});
  }

  private getPreQualificationSelectableItems() {
    this.api.makeRequest('get', 'v1/items')
    .then((response) => {
      this.items = response;
    });
  }

  private updateExpiresAtDate() {
    if (this.expiryDate) {
      this.prequalification.expires_at = this.expiryDate
        .clone()
        .tz(this.app.account.timezone, true)
        .startOf('day')
        .unix();
    } else {
      this.prequalification.expires_at = null;
    }
  }

  onRemoveExpiryDate(evt: any) {
    this.utils.showQuickActions(evt.target, 'Would you like to remove the expiry date? <br>Remember to save after removing it.', [
      {
        text: 'Yes',
        handler: () => {
          this.prequalification.expires_at = null;
          this.expiryDate = null;
        }
      },
      {
        text: 'No',
        handler: () => {}
      }
    ]);
  }

  onSubmit() {

    if ( this.inProgress ) {
      return;
    }

    // Layer 2: Check create vs edit permission based on whether it's a new record
    if (this.prequalification.id) {
      if (!this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_EDIT)
      ], true)) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit pre-qualifications.');
        return;
      }
    } else {
      if (!this.rbac.userCanAccess([
        RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_CREATE)
      ], true)) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to create pre-qualifications.');
        return;
      }
    }

    this.inProgress = true;

    this.prequalification.template = false;

    this.updateExpiresAtDate();

    if (this.expiryDateChanged() && !this.prequalification.expiry_change_note?.trim()) {
      this.utils.showToast('You must enter a note when changing the expiry date.');
      return;
    }

    if ( this.prequalification.id ) {
      this.api.makeRequest('put', `v2/contractor-pre-qualifications/${this.contractorId}`, this.prequalification)
      .then((response) => {
        this.prequalification.expiry_change_note = '';
        this.originalExpiryDate = this.prequalification.expires_at;

        this.utils.showToast('The Pre-Qualification was successfully updated.');
      }).catch(() => {}).finally(() => this.inProgress = false );
    } else {
      this.api.makeRequest('post', `v2/contractor-pre-qualifications`, this.prequalification)
      .then((response) => {
        this.utils.showToast('The Pre-Qualification was successfully created.');
        this.router.navigate([
          `/contractors`, this.contractorId, 'prequalifications'
        ]);
      }).catch(() => {}).finally(() => this.inProgress = false );
    }
  }

  onDelete() {
    if (!this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_CONTRACTORS_PRE_QUALIFICATIONS, RBACService.RBAC_A.A_DELETE)
    ], true)) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete pre-qualifications.');
      return;
    }

    this.utils.showModal(
      'Remove Pre-Qualification',
      `Are you sure you want to remove the "${this.prequalification.name}" Pre-Qualification?`,
      () => {
        this.api.makeRequest('delete', `v2/contractor-pre-qualifications/${this.contractorId}`)
        .then(() => {
          this.utils.showToast('The Pre-Qualification was successfully removed.');
          this.router.navigate(['/contractors', this.contractorId, 'prequalifications']);
        }).catch(() => {});
      }
    );
  }

  onSelectTemplate(template: PrequalificationModel) {
    this.prequalification.name = template.name;
    this.prequalification.description = template.description;
    this.prequalification.items = template.items.map((item) => item.id);
    this.prequalification.enable_notifications = template.enable_notifications;
    this.prequalification.contractor_id = this.contractorId;
  }

  onToggleItem(itemId: number) {
    const index = this.prequalification.items.indexOf(itemId);
    if ( index === -1) {
      this.prequalification.items.push(itemId);
    } else {
      this.prequalification.items.splice(index, 1);
    }
  }

  isItemSelected(itemId: number) {
    if ( this.prequalification.items && this.prequalification.items.length ) {
      return this.prequalification.items.indexOf(itemId) > -1
    } else {
      return false;
    }
  }

  expiryDateChanged(): boolean {
    if (!this.prequalification.id || this.originalExpiryDate === null) {
      return false;
    }

    const tz = this.app.account.timezone;

    if (!this.expiryDate) {
      return this.originalExpiryDate !== null;
    }

    const current = this.expiryDate.clone().tz(tz, true).format('YYYY-MM-DD');
    const original = moment.unix(this.originalExpiryDate).tz(tz).format('YYYY-MM-DD');

    return current !== original;
  }

  protected readonly RBACService = RBACService;
}
